import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserPlus, Mail, Crown, Users, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';

export default function InvitationPage() {
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  
  // Registration form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadInvitation();
  }, [invitationId]);

  const loadInvitation = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/invitations/${invitationId}`);
      setInvitation(response.data);
      setName(response.data.name || '');
      setEmail(response.data.email || '');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invitation invalide ou expirée');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError(t('passwords_dont_match'));
      return;
    }
    
    if (password.length < 8) {
      setError(t('password_min_8'));
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      await api.post('/auth/register', {
        name,
        email,
        password,
        invitation_id: invitationId
      });
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">
              {t('invalid_invitation')}
            </h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/')} variant="outline">
              {t('back_to_home')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
          <Card className="w-full max-w-md">
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2">
                {t('account_created_exclaim')}
              </h2>
              <p className="text-muted-foreground">
                {t('redirecting_login')}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center p-4">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md"
      >
        <Card>
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              {invitation?.type === 'family' ? (
                <Users className="w-8 h-8 text-white" />
              ) : (
                <UserPlus className="w-8 h-8 text-white" />
              )}
            </div>
            <CardTitle className="text-2xl">
              {t('welcome_to_spenspot')}
            </CardTitle>
            
            {/* Invitation details */}
            <div className="mt-4 p-4 bg-muted rounded-lg text-left">
              {invitation?.type === 'family' ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    {t('invited_to_join')}
                  </p>
                  <p className="font-semibold text-lg mt-1">{invitation.family_name}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('role_label')} {invitation.role === 'child' ? (t('child')) : (t('adult'))}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    {t('invited_with_subscription')}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <Crown className="w-5 h-5 text-yellow-500" />
                    <span className="font-semibold">{invitation.subscription_label}</span>
                    {invitation.premium_months > 0 && (
                      <span className="text-sm text-muted-foreground">
                        ({invitation.premium_months} {t('months')})
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium">
                  {t('full_name')}
                </label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('your_name')}
                  required
                  className="mt-1"
                  data-testid="invitation-name"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Email</label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@example.com"
                    required
                    className="pl-10"
                    disabled={invitation?.email}
                    data-testid="invitation-email"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">
                  {t('password')}
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('min_8_chars')}
                  required
                  className="mt-1"
                  data-testid="invitation-password"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">
                  {t('confirm_password')}
                </label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('confirm_password')}
                  required
                  className="mt-1"
                  data-testid="invitation-confirm-password"
                />
              </div>
              
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                disabled={submitting}
                data-testid="invitation-submit"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {t('create_my_account')}
              </Button>
              
              <p className="text-center text-sm text-muted-foreground">
                {t('already_have_account')}{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-indigo-500 hover:underline"
                >
                  {t('sign_in')}
                </button>
              </p>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
